import React, { useState, useCallback } from 'react';
import { FileDropzone } from './FileDropzone';
import { Button } from './Button';
import { ScrollArea } from '@radix-ui/react-scroll-area';
import { FileText, X, Loader2, LayoutGrid, List } from 'lucide-react';
import { useBulkCreateFiles } from '../helpers/useBulkCreateFiles';
import styles from './LocalImportTab.module.css';

interface LocalImportTabProps {
  sessionId: string;
  onClose: () => void;
}

export const LocalImportTab: React.FC<LocalImportTabProps> = ({ sessionId, onClose }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isUploading, setIsUploading] = useState(false);
  const bulkCreate = useBulkCreateFiles();

  const handleFilesSelected = useCallback(async (newFiles: File[]) => {
    const expanded: File[] = [];
    for (const file of newFiles) {
      if (file.name.endsWith('.zip') || file.type === 'application/zip' || file.type === 'application/x-zip-compressed') {
        // Extract ZIP contents
        try {
          const { BlobReader, ZipReader, BlobWriter } = await import('@zip.js/zip.js');
          const reader = new ZipReader(new BlobReader(file));
          const entries = await reader.getEntries();
          for (const entry of entries) {
            if (entry.directory) continue;
            // Skip hidden files, node_modules, .git
            if (entry.filename.startsWith('.') || entry.filename.includes('node_modules/') || entry.filename.includes('.git/')) continue;
            if (entry.getData) {
              const blob = await entry.getData(new BlobWriter());
              const extractedFile = new File([blob], entry.filename, { type: blob.type || 'application/octet-stream' });
              // Preserve directory structure in filename
              (extractedFile as any)._zipPath = entry.filename;
              expanded.push(extractedFile);
            }
          }
          await reader.close();
        } catch (zipErr) {
          console.error('Failed to extract ZIP:', zipErr);
          // Fallback: add the zip file as-is
          expanded.push(file);
        }
      } else {
        expanded.push(file);
      }
    }
    setFiles((prev) => [...prev, ...expanded]);
  }, []);

  // Generate/revoke object URLs for image previews
  React.useEffect(() => {
    // Revoke previous previews
    previews.forEach((p) => p && URL.revokeObjectURL(p));

    const urls = files.map((file) =>
      file.type.startsWith('image/') ? URL.createObjectURL(file) : '',
    );
    setPreviews(urls);

    return () => {
      urls.forEach((u) => u && URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };
  const handleUpload = async () => {
    if (files.length === 0) return;

    setIsUploading(true);
    try {
      const filePromises = files.map(async (file) => {
        // Simple language detection
        const ext = file.name.split('.').pop()?.toLowerCase() || 'plaintext';
        let language = 'plaintext';
        const imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'ico', 'bmp', 'avif'];
        const isImage = imageExtensions.includes(ext);

        // Read images as base64 DataURL, text files as text
        let content: string;
        if (isImage) {
          content = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          language = 'image';
        } else {
          content = await file.text();
          if (['js', 'jsx', 'ts', 'tsx'].includes(ext)) language = 'typescript';
          else if (['py'].includes(ext)) language = 'python';
          else if (['html'].includes(ext)) language = 'html';
          else if (['css'].includes(ext)) language = 'css';
          else if (['json'].includes(ext)) language = 'json';
          else if (['md'].includes(ext)) language = 'markdown';
          else if (['sql'].includes(ext)) language = 'sql';
        }

        // Use ZIP path if extracted from archive, otherwise root
        const zipPath = (file as any)._zipPath;
        let fileName = file.name;
        let filePath = '/';
        if (zipPath) {
          const parts = zipPath.split('/');
          fileName = parts.pop() || file.name;
          filePath = '/' + parts.join('/');
          if (filePath === '/') filePath = '/';
        }

        return {
          fileName,
          filePath,
          content,
          language,
        };
      });

      const filesToCreate = await Promise.all(filePromises);

      await bulkCreate.mutateAsync({
        sessionId,
        files: filesToCreate,
      });

      onClose();
    } catch (error) {
      console.error('Upload failed', error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.dropzoneContainer}>
        <FileDropzone
          onFilesSelected={handleFilesSelected}
          maxFiles={100}
          maxSize={20 * 1024 * 1024} // 20MB each
          accept=".txt,.js,.ts,.tsx,.jsx,.py,.html,.css,.json,.md,.sql,.java,.go,.rs,.png,.jpg,.jpeg,.gif,.webp,.svg,.ico,.bmp,.avif,.zip,.tar,.gz,.xml,.yaml,.yml,.toml,.env,.sh,.bat,.rb,.php,.c,.cpp,.h,.cs,.swift,.kt,.scala,.r,.lua,.dart,.vue,.svelte"
          title="Drag & drop files, images, or ZIP archives"
          subtitle="Up to 100 files, 20MB each. ZIP files auto-extract."
        />
      </div>

      {files.length > 0 && (
        <div className={styles.fileList}>
          <div className={styles.listHeader}>
            <h4 className={styles.listTitle}>Selected Files ({files.length})</h4>
            <div className={styles.viewToggle}>
              <Button
                variant={viewMode === 'grid' ? 'primary' : 'ghost'}
                size="icon-sm"
                onClick={() => setViewMode('grid')}
                aria-label="Grid view"
                className={styles.viewBtn}
              >
                <LayoutGrid size={14} />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'primary' : 'ghost'}
                size="icon-sm"
                onClick={() => setViewMode('list')}
                aria-label="List view"
                className={styles.viewBtn}
              >
                <List size={14} />
              </Button>
            </div>
          </div>

          <div className={`${styles.scrollArea} ${styles.thinScroll}`}>
            {viewMode === 'list' ? (
              files.map((file, index) => (
                <div key={`${file.name}-${index}`} className={styles.fileItem}>
                  <div className={styles.fileInfo}>
                    {previews[index] ? (
                      <img src={previews[index]} alt={file.name} className={styles.thumbnail} />
                    ) : (
                      <FileText size={16} className={styles.fileIcon} />
                    )}
                    <span className={styles.fileName}>{file.name}</span>
                    <span className={styles.fileSize}>{(file.size / 1024).toFixed(1)} KB</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => removeFile(index)}
                    className={styles.removeButton}
                  >
                    <X size={14} />
                  </Button>
                </div>
              ))
            ) : (
              <div className={styles.gridView}>
                {files.map((file, index) => (
                  <div key={`${file.name}-${index}`} className={styles.gridItem}>
                    <div className={styles.gridPreview}>
                      {previews[index] ? (
                        <img src={previews[index]} alt={file.name} className={styles.thumbnail} />
                      ) : (
                        <FileText size={20} className={styles.fileIcon} />
                      )}
                    </div>
                    <div className={styles.gridInfo}>
                      <span className={styles.fileName} title={file.name}>
                        {file.name}
                      </span>
                      <span className={styles.fileSize}>{(file.size / 1024).toFixed(1)} KB</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => removeFile(index)}
                      className={styles.removeButton}
                    >
                      <X size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className={styles.footer}>
        <Button
          onClick={handleUpload}
          disabled={files.length === 0 || isUploading}
          className={styles.uploadButton}
        >
          {isUploading ? (
            <>
              <Loader2 size={16} className={styles.spinner} />
              Uploading...
            </>
          ) : (
            'Upload All'
          )}
        </Button>
      </div>
    </div>
  );
};
